<template>
  <div
    v-if="!useBlurredButton"
    class="we-button flex cursor-pointer"
    @click="action"
    :class="[buttonDisabled, typeClasses,{ 'pointer-events-none': loading }]"
  >
    <div
      v-bind="$attrs"
      class="flex items-center justify-center relative"
      :class="[buttonSpacing, buttonBorders]"
    >
      <!-- Loading -->
      <div v-if="loading && !changePlan" class="pointer-events-none">
        <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <div v-if="loading && changePlan" class="pointer-events-none">
        <svg class="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <!-- Ícone -->
      <we-icon
        v-if="!noIcon && !loading"
        class="p-0 mr-1"
        name="edit"
        :class="weIconClasses"
        :size="iconOptions.size"
      />

      <!-- Label -->
      <span
        v-if="!loading"
        :class="[labelClasses, labelSize]"
        class="font-extrabold"
      >
        {{ label }}
      </span>
    </div>
  </div>

  <!-- Versão Blurred -->
  <Teleport v-else to="body">
    <div class="fixed bottom-0 w-full">
      <we-blurred-box class="w-full">
        <div
          class="we-button flex cursor-pointer w-full"
          @click="action"
          :class="buttonDisabled"
        >
          <div
            v-bind="$attrs"
            class="flex items-center justify-center w-full"
            :class="[buttonSpacing, buttonBorders]"
          >
            <div v-if="loading" class="pointer-events-none">
              <svg
                width="20"
                height="20"
                viewBox="0 0 38 38"
                xmlns="http://www.w3.org/2000/svg"
                stroke="#FFF"
              >
                <g fill="none" fill-rule="evenodd">
                  <g transform="translate(1 1)" stroke-width="2">
                    <circle stroke-opacity=".5" cx="18" cy="18" r="18" />
                    <path d="M36 18c0-9.94-8.06-18-18-18">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 18 18"
                        to="360 18 18"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </path>
                  </g>
                </g>
              </svg>
            </div>

            <we-icon
              v-if="!noIcon && !loading"
              class="p-0 mr-1"
              name="edit"
              :class="weIconClasses"
              :size="iconOptions.size"
            />
            <span
              v-if="!loading"
              :class="[labelClasses, labelSize]"
              class="font-extrabold"
            >
              {{ label }}
            </span>
          </div>
        </div>
      </we-blurred-box>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useScreen } from '@/composables'
import WeIcon from './WeIcon.vue'
import WeBlurredBox from './WeBlurredBox'

const props = defineProps({
  label: String,
  action: Function,
  iconColor: String,
  labelClasses: [String, Object],
  labelColor: String,
  borderColor: { type: String, default: 'primary-color-200' },
  small: Boolean,
  medium: Boolean,
  large: Boolean,
  noBorder: Boolean,
  noIcon: Boolean,
  iconOptions: { type: Object, default: () => ({}) },
  disabled: Boolean,
  useBlurredButton: Boolean,
  loading: { type: Boolean, default: false },
  changePlan: { type: Boolean, default: false },
  card: { type: Boolean, default: false },
  type: { type: String, default: 'default' },
})

const { isSmall } = useScreen()

const useBlurredButton = computed(() => isSmall.value && props.useBlurredButton)

const buttonDisabled = computed(() =>
  props.disabled ? 'opacity-50 pointer-events-none' : ''
)

const buttonSpacing = computed(() => {
  if (props.small) return 'px-3 py-1'
  if (props.medium) return 'px-3 py-2'
  return 'px-3 py-4'
})

const labelSize = computed(() => (props.large ? 'text-lg' : 'text-sm'))

const buttonBorders = computed(() => {
  if (props.noBorder) return ''
  return `border-2 rounded-full border-${props.borderColor}`
})

const typeClasses = computed(() => {
  switch (props.type) {
    case 'plans':
      return 'flex justify-center bg-contrast-brand-300 text-white rounded-full w-full px-6 py-2 ring-8 ring-contrast-brand-200/20'
    default:
      return ''
  }
})

const weIconClasses = computed(() => [
  `fill-${props.iconColor}`,
  props.iconOptions.classes,
])
</script>
